import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { PlusIcon } from '@/components/ui/icons'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import { GalleryFlags } from '@/features/photoset/components/gallery-photoset-modal/gallery-flags/gallery-flags'
import { GalleryMetadata } from '@/features/photoset/components/gallery-photoset-modal/gallery-metadata/gallery-metadata'
import { useGalleryEditingQaFlagsShortcuts } from '@/features/photoset/hooks/use-gallery-editing-qa-flags-shortcuts'
import {
	type Image,
	type PhotosetSpace,
	type Space,
	StepKey
} from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'
import { useTranslation } from '@/translations/i18n'

interface GalleryEditingQaFlagsModalProps {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
	currentSpaceId: PhotosetSpace['id']
	currentSpaceName: Space['key_name']
	isQaView: boolean
}

export const GalleryEditingQaFlagsModal: FC<
	GalleryEditingQaFlagsModalProps
> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose,
	currentSpaceName,
	currentSpaceId,
	isQaView
}) => {
	const { t } = useTranslation()

	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	useGalleryEditingQaFlagsShortcuts({
		selectableImages,
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					kickerTitle={spaceNameToLabel[currentSpaceName]}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<>
							<GalleryMetadata
								imageMetadata={currentImage.metadata}
							/>
							<GalleryFlags
								endSlot={
									isQaView && (
										<FlagsPopover
											disableScrollBelow
											photosetSpaceIds={[currentSpaceId]}
											selectedImagesIds={[
												currentImage.id
											]}
											stepId={StepKey['editing-qa-flags']}
											trigger={
												<Chip
													endSlot={<PlusIcon />}
													isActive={false}
													label={t('common:flag', {
														count: 1
													})}
													variant='info'
												/>
											}
										/>
									)
								}
								image={currentImage}
								isQaView={isQaView}
								photosetSpaceIds={[currentSpaceId]}
								stepId={StepKey['editing-qa-flags']}
							/>
						</>
					}
					title={t('photoset:editingQaFlags.sidebar_title')}
				/>
			}
			thumbnails={currentImage.thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'thumbnails'}
				/>
			}
		/>
	)
}
